import { useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';

export interface UrlHistoryItem {
  url: string;
  title?: string;
  visitedAt: number;
}

const MAX_HISTORY_ITEMS = 20;

export function useUrlHistory() {
  const [history, setHistory] = useLocalStorage<UrlHistoryItem[]>('url-history', []);

  const addToHistory = useCallback((url: string, title?: string) => {
    const normalizedUrl = normalizeUrl(url);

    setHistory((prev) => {
      // Remove existing entry if it exists
      const filtered = prev.filter((item) => item.url !== normalizedUrl);

      // Add new entry at the beginning
      const newHistory = [
        {
          url: normalizedUrl,
          title,
          visitedAt: Date.now(),
        },
        ...filtered,
      ];

      // Keep only the most recent items
      return newHistory.slice(0, MAX_HISTORY_ITEMS);
    });
  }, [setHistory]);

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, [setHistory]);

  const removeFromHistory = useCallback((url: string) => {
    const normalizedUrl = normalizeUrl(url);
    setHistory((prev) => prev.filter((item) => item.url !== normalizedUrl));
  }, [setHistory]);

  return {
    history,
    addToHistory,
    clearHistory,
    removeFromHistory,
  };
}

function normalizeUrl(url: string): string {
  try {
    // Add https:// if no protocol is specified
    let urlToNormalize = url.trim();
    if (!urlToNormalize.match(/^https?:\/\//)) {
      urlToNormalize = `https://${urlToNormalize}`;
    }

    const urlObj = new URL(urlToNormalize);
    return urlObj.toString();
  } catch {
    return url.trim();
  }
}